import { Box, styled } from '@mui/system';
import React from 'react';
import PropTypes from 'prop-types';
import PeopleIcon from '@mui/icons-material/People';
import DashboardIcon from '@mui/icons-material/Dashboard';
import HistoryIcon from '@mui/icons-material/History';
import FavoriteIcon from '@mui/icons-material/Favorite';
import MetaReviewIcon from '@mui/icons-material/FactCheck';
import ArticleIcon from '@mui/icons-material/Newspaper';
import DiscussionIcon from '@mui/icons-material/Forum';
import VideoIcon from '@mui/icons-material/VideoLibrary';
import EpisodeIcon from '@mui/icons-material/Podcasts';
import BookIcon from '@mui/icons-material/MenuBook';
import PaperIcon from '@mui/icons-material/Article';
import TagIcon from '@mui/icons-material/Numbers';
import WebsiteIcon from '@mui/icons-material/Language';
import ToolIcon from '@mui/icons-material/Build';
import FormatListBulleted from '@mui/icons-material/FormatListBulleted';
import DiagramIcon from '@mui/icons-material/AccountTree';
import PublicIcon from '@mui/icons-material/Public';
import CodeIcon from '@mui/icons-material/Code';
import DigestIcon from '@mui/icons-material/Summarize';
import CourseIcon from '@mui/icons-material/School';
import MusicIcon from '@mui/icons-material/MusicNote';
import PresentationIcon from '@mui/icons-material/Slideshow';
import YouTubeChannelIcon from '@mui/icons-material/Subscriptions';
import PodcastIcon from '@mui/icons-material/RssFeed';
import UnknownIcon from '@mui/icons-material/Help';
import {
  Drawer, List, ListItemText, ListItemIcon, ListItemButton,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { selectContextNamespace } from '../redux/features/context/contextSlice';
import { selectPendingCounts, selectPendingTotal, selectModerationActive } from '../redux/features/pending/pendingSlice';
import { MediaTypes, mediaTypeLabels, mediaTypeName, parseMediaType } from '../common/MediaTypes';
import { BrandLink, BrandTitle, MenuIconButton } from './common/appbar/primitives';
import ScopeControl from './common/hero/ScopeControl';
import { GroupLabel } from './common/hero/styles';
import { HEADER_CONTENT_HEIGHT, HOVER_BG, SANS_STACK, TEXT_DARK } from './common/tokens';

const DRAWER_WIDTH = 260;

// The entity grids that offer the `favorites` view.
const FAVORITE_GRIDS = ['items', 'lists', 'authors', 'sites'];

const mediaTypeColor = (type) => mediaTypeLabels.find((l) => l.value === type)?.color;

const StyledDrawer = styled(Drawer)({
  width: DRAWER_WIDTH,
  flexShrink: 0,
  '& .MuiDrawer-paper': {
    width: DRAWER_WIDTH,
    background: '#fff',
    fontFamily: SANS_STACK,
  },
});

const DrawerBox = styled(Box)({
  flex: 1,
  minHeight: 0,
  overflowY: 'auto',
  paddingBottom: '12px',
});

const DrawerHeader = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  height: HEADER_CONTENT_HEIGHT,
  flexShrink: 0,
  padding: '0 14px',
});

const ScopeRow = styled(Box)({
  padding: '4px 12px 10px',
  '& > *': { display: 'flex', width: '100%' },
  '& button': { flex: 1, justifyContent: 'center' },
});

const SectionLabel = styled(GroupLabel)({
  padding: '14px 16px 0',
  marginBottom: '4px',
});

const NavButton = styled(ListItemButton)(({ theme }) => ({
  minHeight: 40,
  margin: '1px 8px',
  padding: '6px 12px',
  borderRadius: 6,
  gap: '16px',
  color: TEXT_DARK,
  '&:hover': { background: HOVER_BG },
  '&.Mui-selected, &.Mui-selected:hover': {
    background: theme.palette.primary.tint,
    '& .MuiListItemText-primary': { fontWeight: 500 },
  },
  '& .MuiListItemIcon-root': { minWidth: 0 },
  '& .MuiSvgIcon-root': { fontSize: 24 },
  '& .MuiListItemText-root': { minWidth: 0 },
  '& .MuiListItemText-primary': {
    fontSize: 14,
    fontWeight: 400,
    lineHeight: '20px',
    letterSpacing: 'normal',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
}));

const NavCount = styled('span')(({ theme }) => ({
  padding: '3px 7px',
  borderRadius: '6px',
  background: theme.palette.primary.main,
  color: '#fff',
  fontSize: 12,
  fontWeight: 700,
  lineHeight: 1,
}));

const ListRoot = styled(List)({
  width: '100%',
  padding: 0,
});

function ViewDrawer(props) {
  const { drawerOpen, setDrawerOpen } = props;
  const navigate = useNavigate();
  const location = useLocation();
  const namespace = useSelector(selectContextNamespace);
  const counts = useSelector(selectPendingCounts);
  const pendingTotal = useSelector(selectPendingTotal);
  const moderationActive = useSelector(selectModerationActive);
  const { userInfo } = useSelector((state) => state.users);
  const isModerator = !!userInfo?.isModerator;
  const { pathname } = location;
  let namespacePath;
  if (pathname.startsWith('/moderate')) namespacePath = 'moderate';
  else if (pathname.startsWith('/explore')) namespacePath = 'explore';
  else if (pathname.startsWith('/learn')) namespacePath = 'learn';
  else if (namespace === 'pending') namespacePath = 'moderate';
  else if (namespace === 'public') namespacePath = 'explore';
  else namespacePath = 'learn';
  const isModerate = namespacePath === 'moderate';
  const isExplore = namespacePath === 'explore';
  const moderationMode = isModerator && pendingTotal > 0 && (moderationActive || isModerate);
  const currentType = new URLSearchParams(location.search).get('type');
  const currentView = new URLSearchParams(location.search).get('view');

  const handleNamespaceToggle = () => {
    const currentPath = location.pathname;
    let newPath;
    if (isExplore) {
      newPath = currentPath.replace(/^\/explore/, '/learn');
    } else {
      newPath = currentPath.replace(/^\/learn/, '/explore');
    }
    if (newPath === currentPath) {
      newPath = isExplore ? '/learn' : '/explore';
    }
    navigate(newPath);
  };

  const entityItems = [
    { key: 'items', label: 'Items', icon: <DashboardIcon sx={{ color: 'primary.main' }} />, path: `/${namespacePath}/items`, count: isModerate ? counts.items : undefined },
    { key: 'lists', label: 'Lists', icon: <FormatListBulleted sx={{ color: 'primary.main' }} />, path: `/${namespacePath}/lists`, count: isModerate ? counts.lists : undefined },
    { key: 'authors', label: 'Authors', icon: <PeopleIcon sx={{ color: 'primary.main' }} />, path: `/${namespacePath}/authors`, count: isModerate ? counts.authors : undefined },
    { key: 'sites', label: 'Sites', icon: <WebsiteIcon sx={{ color: 'primary.main' }} />, path: `/${namespacePath}/sites`, count: isModerate ? counts.sites : undefined },
    { key: 'tags', label: 'Tags', icon: <TagIcon sx={{ color: 'primary.main' }} />, path: `/${namespacePath}/tags` },
  ];

  // Every entity grid offers favorites. The entry follows the one you are
  // on and falls back to items elsewhere. History is items-only.
  const currentGrid = FAVORITE_GRIDS
    .find((key) => pathname === `/${namespacePath}/${key}`) || 'items';

  // History/Favorites belong to My Library only — not Explore, not Moderation.
  // So does the metadata review queue (for moderators only).
  const specialItems = (!isExplore && !isModerate) ? [
    { key: 'history', label: 'History', icon: <HistoryIcon sx={{ color: 'green' }} />, view: 'history', grid: 'items' },
    { key: 'favorites', label: 'Favorites', icon: <FavoriteIcon sx={{ color: 'primary.main' }} />, view: 'favorites', grid: currentGrid },
    ...(isModerator ? [
      { key: 'metareview', label: 'Review', icon: <MetaReviewIcon sx={{ color: 'primary.main' }} />, view: 'metareview', grid: 'items' },
    ] : []),
  ].map((item) => ({ ...item, path: `/learn/${item.grid}?view=${item.view}` })) : [];

  const categoryItems = [
    { key: 'articles', label: 'Articles', icon: <ArticleIcon sx={{ color: mediaTypeColor(MediaTypes.Article) }} />, type: MediaTypes.Article },
    { key: 'websites', label: 'Websites', icon: <PublicIcon sx={{ color: mediaTypeColor(MediaTypes.Website) }} />, type: MediaTypes.Website },
    { key: 'videos', label: 'Videos', icon: <VideoIcon sx={{ color: mediaTypeColor(MediaTypes.Video) }} />, type: MediaTypes.Video },
    { key: 'episodes', label: 'Episodes', icon: <EpisodeIcon sx={{ color: mediaTypeColor(MediaTypes.Episode) }} />, type: MediaTypes.Episode },
    { key: 'books', label: 'Books', icon: <BookIcon sx={{ color: mediaTypeColor(MediaTypes.Book) }} />, type: MediaTypes.Book },
    { key: 'papers', label: 'Papers', icon: <PaperIcon sx={{ color: mediaTypeColor(MediaTypes.Paper) }} />, type: MediaTypes.Paper },
    { key: 'discussions', label: 'Discussions', icon: <DiscussionIcon sx={{ color: mediaTypeColor(MediaTypes.Discussion) }} />, type: MediaTypes.Discussion },
    { key: 'tools', label: 'Tools', icon: <ToolIcon sx={{ color: mediaTypeColor(MediaTypes.Tool) }} />, type: MediaTypes.Tool },
    { key: 'diagrams', label: 'Diagrams', icon: <DiagramIcon sx={{ color: mediaTypeColor(MediaTypes.Diagram) }} />, type: MediaTypes.Diagram },
    { key: 'code', label: 'Code', icon: <CodeIcon sx={{ color: mediaTypeColor(MediaTypes.Code) }} />, type: MediaTypes.Code },
    { key: 'digests', label: 'Digests', icon: <DigestIcon sx={{ color: mediaTypeColor(MediaTypes.Digest) }} />, type: MediaTypes.Digest },
    { key: 'courses', label: 'Courses', icon: <CourseIcon sx={{ color: mediaTypeColor(MediaTypes.Course) }} />, type: MediaTypes.Course },
    { key: 'music', label: 'Music', icon: <MusicIcon sx={{ color: mediaTypeColor(MediaTypes.Music) }} />, type: MediaTypes.Music },
    { key: 'presentations', label: 'Presentations', icon: <PresentationIcon sx={{ color: mediaTypeColor(MediaTypes.Presentation) }} />, type: MediaTypes.Presentation },
    { key: 'channels', label: 'Channels', icon: <YouTubeChannelIcon sx={{ color: mediaTypeColor(MediaTypes.YouTubeChannel) }} />, type: MediaTypes.YouTubeChannel },
    { key: 'podcasts', label: 'Podcasts', icon: <PodcastIcon sx={{ color: mediaTypeColor(MediaTypes.Podcast) }} />, type: MediaTypes.Podcast },
  ].map((item) => ({ ...item, path: `/${namespacePath}/items?type=${mediaTypeName(item.type)}` }));

  const unknownItem = {
    key: 'unknown', label: 'Unknown', icon: <UnknownIcon sx={{ color: 'red' }} />, view: 'unknown', path: `/${namespacePath}/items?view=unknown`,
  };

  // Types and named views both ride on an entity grid as a query param, so an
  // entry matches only when the grid it points at is the one on show.
  const onItemsGrid = pathname === `/${namespacePath}/items`;
  const gridOf = (path) => path.split('?')[0];
  const renderMenuItem = (item) => {
    let selected;
    if (item.type != null) selected = onItemsGrid && parseMediaType(currentType) === item.type;
    else if (item.view) selected = pathname === gridOf(item.path) && currentView === item.view;
    else selected = pathname === item.path && !currentType && !currentView;
    return (
      <NavButton
        key={item.key}
        selected={selected}
        onClick={() => { navigate(item.path); setDrawerOpen(false); }}
      >
        <ListItemIcon>{item.icon}</ListItemIcon>
        <ListItemText primary={item.label} />
        {item.count > 0 && <NavCount>{item.count}</NavCount>}
      </NavButton>
    );
  };

  // The scope segments mirror the header's tabs: where you are, and the one
  // click that moves you. Moderate only exists while the queue is non-empty.
  const scopeOptions = [
    { value: 'learn', label: 'Library', title: 'My Library' },
    { value: 'explore', label: 'Explore', title: 'Public content' },
    ...(moderationMode
      ? [{ value: 'moderate', label: 'Moderate', count: pendingTotal, title: 'Pending queue' }]
      : []),
  ];

  const handleScopeChange = (scope) => {
    if (scope === namespacePath) return;
    if (scope === 'moderate') {
      navigate('/moderate/items');
    } else if (isModerate) {
      navigate(scope === 'explore' ? '/explore' : '/learn');
    } else {
      handleNamespaceToggle();
    }
  };

  return (
    <StyledDrawer
      open={drawerOpen}
      variant="temporary"
      onClose={() => setDrawerOpen(false)}
      ModalProps={{
        keepMounted: true,
      }}
    >
      <DrawerHeader>
        <MenuIconButton onClick={() => setDrawerOpen(false)}>
          <MenuIcon />
        </MenuIconButton>
        <BrandLink to="/explore" onClick={() => setDrawerOpen(false)}>
          <BrandTitle color="primary">Curated</BrandTitle>
          <BrandTitle color="secondary">List</BrandTitle>
        </BrandLink>
      </DrawerHeader>
      <ScopeRow>
        <ScopeControl
          options={scopeOptions}
          value={namespacePath}
          onChange={handleScopeChange}
        />
      </ScopeRow>
      <DrawerBox>
        <ListRoot>
          <SectionLabel>Browse</SectionLabel>
          {entityItems.map(renderMenuItem)}
          {specialItems.length > 0 && (
            <>
              <SectionLabel>Personal</SectionLabel>
              {specialItems.map(renderMenuItem)}
            </>
          )}
          <SectionLabel>Media types</SectionLabel>
          {categoryItems.map(renderMenuItem)}
          {renderMenuItem(unknownItem)}
        </ListRoot>
      </DrawerBox>
    </StyledDrawer>
  );
}

ViewDrawer.propTypes = {
  drawerOpen: PropTypes.bool.isRequired,
  setDrawerOpen: PropTypes.func.isRequired,
};

export default ViewDrawer;
